<template>
  <div class="p-6 dark:bg-text-900">
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center">
        <button
          @click="$router.push('/users')"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Users
        </button>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">User Details</h1>
      </div>
      <router-link v-if="canChange || currentUser?.is_staff"
        :to="{ name: 'EditUser', params: { id: user.id } }"
        class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 "
      >
        Edit User
      </router-link>
    </div>

    <!-- Error Message -->
    <div
        v-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
        role="alert"
        >
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">{{ error }}</span>
    </div>

        <!-- Success Message -->
    <div
        v-if="successMessage"
        class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
        role="alert"
        >
        <strong class="font-bold">Success!</strong>
        <span class="block sm:inline">{{ successMessage }}</span>
    </div>

        <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">
          Loading account details...
    </div>

    <div
      v-if="showPasswordModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 overflow-y-auto h-full w-full flex items-center justify-center"
      >
      <div class="relative glass-card p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4 dark:text-white">Set Password</h3>
        <form @submit.prevent="handlePasswordChange">
          <div class="space-y-4">
            <div class="dark:text-gray-50">
              <ul class="list-disc list-inside space-y-1">
                <li>The password cannot be too similar to your other personal information.</li>
                <li>The password must contain at least 8 characters.</li>
                <li>The password cannot be a commonly used password.</li>
                <li>The password cannot be entirely numeric.</li>
              </ul>
            </div>
            <div class="text-center space-y-3">
              <div>
                <label class="block text-sm text-gray-600 dark:text-white">New Password</label>
                <input
                  v-model="passwordForm.new_password"
                  type="password"
                  required
                  :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['confirm_password']
                    ? 'ring-2 ring-red-500  focus:ring-red-500'
                    : 'focus:ring-blue-500'
                  ]"
                />
              </div>
              
              <div>
                <label class="block text-sm text-gray-600 dark:text-white"
                  >Confirm New Password</label
                >
                <input
                  v-model="passwordForm.confirm_password"
                  type="password"
                  required
                :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['confirm_password']
                    ? 'ring-2 ring-red-500  focus:ring-red-500'
                    : 'focus:ring-blue-500'
                  ]"
                />
                <FieldError :message="fieldErrors['new_password2']" />
                <FieldError :message="fieldErrors['confirm_password']" />
              </div>
            </div>
          </div>
          <div class="mt-6 flex gap-4">
            <button
              type="button"
              @click="cancelPasswordSet"
              class="btn bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn bg-blue-600 text-white hover:bg-blue-700"
              :disabled="isChangingPassword"
            >
              {{ isChangingPassword ? "Changing..." : "Submit Password" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div class="glass-card p-6">
      <div class="px-4 py-5 sm:px-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white">User Information</h3>
        <p class="mt-1 max-w-2xl text-sm text-gray-500 dark:text-white">
          Personal details and account information.
        </p>
      </div>
      <div class="border-t border-gray-200 dark:border-gray-600">
        <dl>
          <div
            class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Username/Email</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 dark:text-white">
              {{ user.username }}
            </dd>
          </div>
          <div
            class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Full name</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 dark:text-white">
              {{ user.first_name || "-" }} {{ user.last_name || "" }}
            </dd>
          </div>
          <div
            class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Organisation ID</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
              {{ user.org_name }}
            </dd>
          </div>
          <div
            class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Account Status</dt>
            <dd class="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span
                :class="
                  user.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                "
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ user.is_active ? "Active" : "Inactive" }}
              </span>
            </dd>
          </div>
          <div
            class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Account Type</dt>
            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <div class="space-y-1">
                <div class="space-x-1">
                  <span
                    class="bg-blue-100 text-blue-800 px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    {{ user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "No role" }}
                  </span>
                  <span
                    v-if="user.is_subscribed_to_mivulite"
                    class="bg-orange-100 text-orange-800 px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    Mivulite
                  </span>
                </div>
              </div>
            </dd>
          </div>
          <div
            class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Date Joined</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
              {{ formatDate(user.date_joined) }}
            </dd>
          </div>
          <div
            class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Last Login</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
              {{ user.last_login ? formatDate(user.last_login) : "Never" }}
            </dd>
          </div>
          <div
            class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">
              Last Password Change
            </dt>
            <dd class="mt-1 text-sm text-gray-900  dark:text-whitesm:mt-0 sm:col-span-2">
              {{ formatDate(user.last_password_change_date) }}
            </dd>
          </div>
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800">
            <dt class="text-sm font-medium text-gray-700 dark:text-white">
               Set Password
            </dt>
              <dd>
              <button v-if="canChange || currentUser?.is_staff"
                @click="showPasswordModal = true"
                class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                Set Password
              </button>
            </dd>
          </div>

          <div
            v-if="user.is_staff || user.is_superuser || ['editor', 'admin', 'staff'].includes(user.role)"
            class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 dark:bg-gray-800"
          >
            <dt class="text-sm font-medium text-gray-700 dark:text-white">Commit Notifications</dt>
            <dd class="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span
                :class="user.receive_commit_notifications ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ user.receive_commit_notifications ? 'Enabled' : 'Disabled' }}
              </span>
              <span v-if="user.receive_commit_notifications_updated_at" class="ml-2 text-sm text-gray-600 dark:text-gray-300">
                Last changed {{ formatDate(user.receive_commit_notifications_updated_at) }}
              </span>
            </dd>
          </div>

        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserById } from "@/services/userService";
import { getAllOrgs } from "@/services/orgService";
import { setPassword } from "@/services/authService";
import { useStore } from "@/store";
import FieldError from "@/components/FieldError.vue";

export default {
  name: "UserDetails",
  components: {
    FieldError
  },
  data() {
    return {
      user: this.getEmptyUserObject(),
      loading: false,
      error: null,
      organizations: {},
      isLoading: "",
      successMessage: "",
      isChangingPassword :false,
      showDeleteModal : false,
      showPasswordModal: false,
      passwordForm : {
        new_password: "",
        confirm_password: "",
      },
      canChange: false,
      fieldErrors: {},
    };
  },

  async created() {
    const userId = this.$route.params.id;
    await this.checkPermissions();
    await this.fetchUser(userId);
    await this.fetchOrganizations();
  },
  computed: {
    currentUser() {
      return useStore.getUser();
    },
  },
  watch: {
    currentUser(currentUser){
      if (currentUser){
        this.checkPermissions();
      }
    }
  },
  methods: {
    cancelPasswordSet(){
      this.fieldErrors = {};
      this.showPasswordModal = false;
      this.passwordForm = {
        new_password: "",
        confirm_password: "",
      }
    },
    async checkPermissions() {
      if (this.currentUser) {
        this.canChange = useStore.hasPermission("Can change user");
      }
    },
    async handlePasswordChange(){
      this.fieldErrors = {};
      if (
        this.passwordForm.new_password !== this.passwordForm.confirm_password
      ) {
        const error = "New passwords do not match";
        this.fieldErrors = { ...this.fieldErrors, "confirm_password": error}
        return;
      }
      this.isChangingPassword = true;
      this.error = null;
      this.successMessage = null;
      try {
        await setPassword(this.$route.params.id,{
          new_password: this.passwordForm.new_password,
          confirm_password: this.passwordForm.confirm_password
        });
        this.showPasswordModal = false;
        this.successMessage = "Password changed successfully";
        this.passwordForm = {
          new_password: "",
          confirm_password: "",
        };
        
      } catch (err) {
          this.fieldErrors = {}; 
          const errData = err?.response?.data;

          for (const element in errData){
            const fieldError = errData?.[element];
            if (fieldError?.[0]) {
              this.fieldErrors[element] = fieldError[0]; 
            }
          }
      } finally {
        this.isChangingPassword = false;
      } 
    },

    getEmptyUserObject() {
      return {
       
        id: null,
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        is_active: true,
        is_staff: false,
        is_superuser: false,
        org: "",
        user_permissions: [],
        org_name: ""
      };
    },
    async fetchUser(userId) {
      try {
        const userData = await getUserById(userId);
        this.user = userData;
        
      } catch (error) {
        console.error("Error fetching user:", error);
        this.error = "Failed to load user";
      }
    },
    async fetchOrganizations() {
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs.reduce((acc, org) => {
          acc[org.id] = org;
          return acc;
        }, {});
        this.org_name = this.organizations[this.user.org]?.name;
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("default", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(date);
    },
  },
};
</script>
